import { useState } from 'react';
import { PRICING_MATRIX, COMPANY_INFO } from '../data/staticData';
import { submitWebform } from '../lib/cmsClient';

export default function PriceCalculator({ compact = false }) {
  const [step, setStep] = useState(1);
  const [selectedRoofType, setSelectedRoofType] = useState('eternittag');
  const [slope, setSlope] = useState('12-34'); // '12-34' | '35-50'
  const [areaM2, setAreaM2] = useState(130);
  const [angle, setAngle] = useState('rektangulaer'); // 'vinkel' | 'rektangulaer'

  // Contact form state
  const [formState, setFormState] = useState({
    Navn: '',
    Mail: '',
    Mobil: '',
    Adresse: '',
    Postnummer: '',
    Besked: '',
    Finansiering: false,
    AccepterBetingelser: false,
    website_url: '' // Antispam honeypot
  });

  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  // Beregning baseret på Dansk Tagbygs officielle formel og enhedspriser fra Excel:
  // Grundplan (m²) * Enhedspris (hvor enhedspris = basistakst * hældningstillæg * formtillæg)
  const calculateEstimate = () => {
    const selectedRoof = PRICING_MATRIX.roofTypes.find(r => r.id === selectedRoofType) || PRICING_MATRIX.roofTypes[0];
    const slopeObj = PRICING_MATRIX.slopes.find(s => s.id === slope) || PRICING_MATRIX.slopes[0];
    const angleObj = PRICING_MATRIX.angles?.find(a => a.id === angle) || PRICING_MATRIX.angles[0];

    // Enhedspris pr m2 iht. arket (f.eks. Tegltag 12-34° rektangulær = 1.457,50 kr.)
    const unitPriceM2 = selectedRoof.pricePerM2 * slopeObj.multiplier * angleObj.multiplier;
    const totalCost = areaM2 * unitPriceM2;

    return { total: totalCost, unitPriceM2 };
  };

  const estimate = calculateEstimate();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    setFileError('');
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    const totalAllowed = 5;
    const combinedFiles = [...files, ...selectedFiles].slice(0, totalAllowed);

    if (files.length + selectedFiles.length > totalAllowed) {
      setFileError(`Du kan maksimalt vedhæfte op til ${totalAllowed} billeder i alt.`);
    }

    // Check size & types
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
    for (const f of combinedFiles) {
      if (f.size > 10 * 1024 * 1024) {
        setFileError(`Billedet "${f.name}" er større end tilladt (maks 10 MB per fil).`);
        return;
      }
      if (!allowedTypes.includes(f.type) && !/\.(jpe?g|png|webp|gif|pdf)$/i.test(f.name)) {
        setFileError(`Filen "${f.name}" har et ugyldigt format. Tilladte formater er JPG, PNG, WEBP, GIF og PDF.`);
        return;
      }
    }

    const totalSize = combinedFiles.reduce((acc, f) => acc + f.size, 0);
    if (totalSize > 20 * 1024 * 1024) {
      setFileError('De samlede filer overskrider grænsen på 20 MB.');
      return;
    }

    setFiles(combinedFiles);
    // Reset file input value so user can re-select if needed
    e.target.value = '';
  };

  const removeFile = (indexToRemove) => {
    setFiles(prev => prev.filter((_, idx) => idx !== indexToRemove));
    setFileError('');
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formState.AccepterBetingelser) {
      alert('Du skal acceptere vilkårene for brug af oplysninger og privatlivspolitik for at sende.');
      return;
    }

    setSubmitting(true);
    setSubmitStatus(null);

    const selectedRoofObj = PRICING_MATRIX.roofTypes.find(r => r.id === selectedRoofType);
    const slopeObj = PRICING_MATRIX.slopes.find(s => s.id === slope);
    const angleObj = PRICING_MATRIX.angles?.find(a => a.id === angle);

    const beskedTekst = `[PRISBEREGNER FORESPØRGSEL]
1. Valgt tagtype: ${selectedRoofObj?.name || selectedRoofType}
2. Tagets hældning: ${slopeObj?.name || slope}
3. Husets grundplan: ${areaM2} m²
4. Tagets form: ${angleObj?.name || angle}
Enhedspris: ${estimate.unitPriceM2.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr./m²
Vejledende beregnet pris: ${estimate.total.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr. inkl. moms
Finansiering på plads: ${formState.Finansiering ? 'Ja' : 'Nej/Ikke oplyst'}
Vedhæftede billeder: ${files.length > 0 ? `${files.length} stk.` : 'Ingen'}
Evt. bemærkninger: ${formState.Besked || 'Ingen'}`;

    let res;
    if (files.length > 0) {
      const formData = new FormData();
      formData.append('Navn', formState.Navn);
      formData.append('Mail', formState.Mail);
      formData.append('Mobil', formState.Mobil);
      formData.append('Adresse', formState.Adresse);
      formData.append('Postnummer', formState.Postnummer);
      formData.append('Besked', beskedTekst);
      formData.append('Finansiering', formState.Finansiering ? 'Ja' : 'Nej');
      formData.append('website_url', formState.website_url); // Honeypot

      files.forEach((file) => {
        formData.append('billeder', file);
      });

      res = await submitWebform(formData);
    } else {
      const payload = {
        Navn: formState.Navn,
        Mail: formState.Mail,
        Mobil: formState.Mobil,
        Adresse: formState.Adresse,
        Postnummer: formState.Postnummer,
        Besked: beskedTekst,
        Finansiering: formState.Finansiering,
        website_url: formState.website_url // Honeypot
      };
      res = await submitWebform(payload);
    }

    setSubmitting(false);

    if (res.ok) {
      setSubmitStatus('success');
    } else {
      setSubmitStatus('error');
    }
  };

  const activeRoof = PRICING_MATRIX.roofTypes.find(r => r.id === selectedRoofType) || PRICING_MATRIX.roofTypes[0];
  const activeSlope = PRICING_MATRIX.slopes.find(s => s.id === slope) || PRICING_MATRIX.slopes[0];
  const activeAngle = PRICING_MATRIX.angles?.find(a => a.id === angle) || { id: 'rektangulaer', name: 'Rektangulær' };

  return (
    <div className={`bg-white rounded-none shadow-xl overflow-hidden ${compact ? 'p-4 sm:p-6' : 'px-4 sm:px-8 lg:px-12 py-8 sm:py-12'}`}>
      
      {/* Header */}
      <div className="text-center max-w-4xl mx-auto mb-8 sm:mb-12">
        <span className="text-xs uppercase tracking-widest font-extrabold text-[#42b732] mb-2 block">
          Prisberegner
        </span>
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-stone-900 tracking-tight uppercase">
          Hvad koster et nyt tag?
        </h2>
        <p className="text-stone-600 text-base sm:text-lg mt-3 leading-relaxed max-w-2xl mx-auto">
          Prøv vores prisberegner på nyt tag herunder og få et vejledende estimat samt uforpligtende tilbud ud fra dine oplysninger.
        </p>
      </div>

      {/* Progress Stepper */}
      <div className="mb-8 sm:mb-12 max-w-4xl mx-auto">
        <div className="grid grid-cols-5 text-center text-xs sm:text-sm font-bold text-stone-400 mb-3 gap-1">
          <button
            type="button"
            onClick={() => setStep(1)}
            className={`transition-colors cursor-pointer text-left sm:text-center ${step >= 1 ? 'text-[#42b732] font-black' : ''}`}
          >
            1. Tagtype
          </button>
          <button
            type="button"
            onClick={() => setStep(2)}
            className={`transition-colors cursor-pointer text-center ${step >= 2 ? 'text-[#42b732] font-black' : ''}`}
          >
            2. Hældning
          </button>
          <button
            type="button"
            onClick={() => setStep(3)}
            className={`transition-colors cursor-pointer text-center ${step >= 3 ? 'text-[#42b732] font-black' : ''}`}
          >
            3. Grundplan
          </button>
          <button
            type="button"
            onClick={() => setStep(4)}
            className={`transition-colors cursor-pointer text-center ${step >= 4 ? 'text-[#42b732] font-black' : ''}`}
          >
            4. Vinkel
          </button>
          <button
            type="button"
            onClick={() => setStep(5)}
            className={`transition-colors cursor-pointer text-right sm:text-center ${step >= 5 ? 'text-[#42b732] font-black' : ''}`}
          >
            5. Pris
          </button>
        </div>
        <div className="w-full bg-stone-200 h-2.5 rounded-full overflow-hidden">
          <div
            className="bg-[#42b732] h-full transition-all duration-300"
            style={{ width: `${(step / 5) * 100}%` }}
          />
        </div>
      </div>

      {/* STEP 1: 1. Vælg den ønskede tagtype */}
      {step === 1 && (
        <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn">
          <div className="text-center space-y-1">
            <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
              1. Vælg den ønskede tagtype
            </h3>
            <p className="text-stone-500 text-sm">
              Klik på det materiale, du ønsker på dit nye tag.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PRICING_MATRIX.roofTypes.map((roof) => {
              const isSelected = selectedRoofType === roof.id;
              return (
                <button
                  key={roof.id}
                  type="button"
                  onClick={() => setSelectedRoofType(roof.id)}
                  className={`group relative rounded-none border text-left transition-all duration-200 flex flex-col overflow-hidden cursor-pointer ${
                    isSelected
                      ? 'border-2 border-[#42b732] ring-2 ring-[#42b732]/30 shadow-md bg-stone-50/50'
                      : 'border border-stone-300 bg-white hover:border-[#42b732]/60 hover:shadow-sm'
                  }`}
                >
                  {/* Image */}
                  <div className="relative w-full h-36 sm:h-40 bg-stone-100 overflow-hidden">
                    <img
                      src={roof.image}
                      alt={roof.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                    {isSelected && (
                      <div className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-[#42b732] text-white flex items-center justify-center text-xs shadow-md ring-2 ring-white">
                        <i className="fa-solid fa-check"></i>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-3.5 sm:p-4 flex flex-col flex-grow justify-between">
                    <div>
                      <div className="font-bold text-sm sm:text-base text-stone-900 leading-snug">
                        {roof.name}
                      </div>
                      <div className="text-xs text-stone-500 mt-1 leading-relaxed">
                        {roof.description}
                      </div>
                    </div>
                    <div className="mt-3 pt-2 border-t border-stone-200 flex items-center justify-between text-xs">
                      <span className="text-stone-500">Prisniveau</span>
                      <span className={`font-bold ${isSelected ? 'text-[#42b732]' : 'text-stone-700'}`}>
                        Fra ca. {roof.pricePerM2.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr/m²
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="button"
              onClick={() => setStep(2)}
              className="px-6 py-3 rounded-none bg-[#42b732] hover:bg-[#389e2b] text-white font-bold transition-all flex items-center gap-2 shadow-md hover:shadow-lg cursor-pointer"
            >
              Næste: Tagets hældning <i className="fa-solid fa-arrow-right"></i>
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: 2. Vælg tagets hældning */}
      {step === 2 && (
        <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn">
          <div className="text-center space-y-1">
            <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
              2. Vælg tagets hældning
            </h3>
            <p className="text-stone-500 text-sm">
              Tagets hældning har betydning for tagfladens samlede areal og krav til undertag.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto pt-4">
            {PRICING_MATRIX.slopes.map((s) => {
              const isSelected = slope === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSlope(s.id)}
                  className={`rounded-none border text-left transition-all duration-200 flex flex-col overflow-hidden cursor-pointer relative ${
                    isSelected
                      ? 'border-2 border-[#42b732] bg-[#42b732]/5 text-stone-950 shadow-md ring-1 ring-[#42b732]'
                      : 'border border-stone-300 bg-white text-stone-800 hover:border-[#42b732]/50 hover:bg-stone-50'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute top-3 right-3 z-10 w-6 h-6 rounded-full bg-[#42b732] text-white flex items-center justify-center text-xs shadow">
                      <i className="fa-solid fa-check"></i>
                    </div>
                  )}
                  {s.image ? (
                    <div className="h-40 w-full overflow-hidden bg-white relative flex items-center justify-center p-4 border-b border-stone-100">
                      {s.id === '35-50' ? (
                        /* Clean geometric roof angle for 35 - 50 degrees matching Dansk Tagbyg style */
                        <svg
                          viewBox="0 0 160 100"
                          className="w-40 h-28 text-[#333333] transition-transform duration-300 group-hover:scale-105"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <polyline points="20,82 80,18 140,82" />
                        </svg>
                      ) : (
                        <svg
                          viewBox="0 0 160 100"
                          className="w-40 h-28 text-[#333333] transition-transform duration-300 group-hover:scale-105"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <polyline points="15,68 80,38 145,68" />
                        </svg>
                      )}
                    </div>
                  ) : (
                    <div className="w-full h-36 bg-stone-100 flex items-center justify-center text-3xl text-stone-600">
                      <i className={s.id === '12-34' ? 'fa-solid fa-angle-up' : 'fa-solid fa-mountain'}></i>
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1 justify-between">
                    <div>
                      <div className="text-2xl font-black text-stone-900 mb-1">{s.name}</div>
                      <div className="text-sm font-semibold text-stone-700">{s.description}</div>
                      <p className="text-xs text-stone-500 mt-2">
                        {s.id === '12-34' ? 'Almindelig standard parcelhustaghældning' : 'Stejlere taghældning med større tagflade'}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex justify-between pt-6">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="px-5 py-2.5 rounded-none bg-stone-200 hover:bg-stone-300 text-stone-800 font-bold transition-all cursor-pointer"
            >
              Tilbage
            </button>
            <button
              type="button"
              onClick={() => setStep(3)}
              className="px-6 py-3 rounded-none bg-[#42b732] hover:bg-[#389e2b] text-white font-bold transition-all flex items-center gap-2 shadow-md hover:shadow-lg cursor-pointer"
            >
              Næste: Husets grundplan <i className="fa-solid fa-arrow-right"></i>
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: 3. Angiv størrelsen på husets grundplan */}
      {step === 3 && (
        <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn">
          <div className="text-center space-y-1">
            <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
              3. Angiv størrelsen på husets grundplan
            </h3>
            <p className="text-stone-500 text-sm">
              Indtast eller juster husets bebyggede areal i kvadratmeter.
            </p>
          </div>

          <div className="bg-stone-50 p-6 sm:p-8 rounded-none border border-stone-200 space-y-6 max-w-2xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <label htmlFor="areaInput" className="font-bold text-stone-900 text-base sm:text-lg">
                Husets grundplan:
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="areaInput"
                  type="number"
                  min="40"
                  max="500"
                  step="5"
                  value={areaM2}
                  onChange={(e) => setAreaM2(Math.max(40, Number(e.target.value) || 40))}
                  className="w-28 px-3 py-2 rounded-none bg-white border border-stone-300 text-stone-900 font-bold text-right text-xl focus:outline-none focus:ring-2 focus:ring-[#42b732] focus:border-[#42b732]"
                />
                <span className="text-stone-800 font-bold text-lg">m²</span>
              </div>
            </div>

            <input
              type="range"
              min="50"
              max="400"
              step="5"
              value={areaM2}
              onChange={(e) => setAreaM2(Number(e.target.value))}
              className="w-full accent-[#42b732] bg-stone-200 rounded-lg h-3 cursor-pointer"
            />

            <div className="flex justify-between text-xs text-stone-500">
              <span>50 m² (Mindre bolig / fritidshus)</span>
              <span>150 m² (Klassisk parcelhus)</span>
              <span>400 m² (Større villa / ejendom)</span>
            </div>
          </div>

          <div className="flex justify-between pt-6">
            <button
              type="button"
              onClick={() => setStep(2)}
              className="px-5 py-2.5 rounded-none bg-stone-200 hover:bg-stone-300 text-stone-800 font-bold transition-all cursor-pointer"
            >
              Tilbage
            </button>
            <button
              type="button"
              onClick={() => setStep(4)}
              className="px-6 py-3 rounded-none bg-[#42b732] hover:bg-[#389e2b] text-white font-bold transition-all flex items-center gap-2 shadow-md hover:shadow-lg cursor-pointer"
            >
              Næste: Tagets vinkel <i className="fa-solid fa-arrow-right"></i>
            </button>
          </div>
        </div>
      )}

      {/* STEP 4: 4. Vælg tagets vinkel */}
      {step === 4 && (
        <div className="max-w-2xl mx-auto space-y-6 animate-fadeIn font-sans">
          {/* Step Header matching reference */}
          <div className="flex items-center gap-3.5 mb-6">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white text-base font-extrabold text-neutral-900 shadow-md shadow-black/10 ring-1 ring-black/5">
              4
            </span>
            <h2 className="text-xl font-bold tracking-tight text-neutral-900">
              Vælg tagets vinkel
            </h2>
          </div>

          {/* Options Grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Option 1: Vinkel */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => setAngle('vinkel')}
              onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') setAngle('vinkel'); }}
              className={`group relative flex cursor-pointer items-center justify-between p-3 transition-all duration-200 ${
                angle === 'vinkel'
                  ? 'bg-[#ECECEC] ring-2 ring-[#42b732] shadow-sm'
                  : 'bg-[#ECECEC] hover:bg-neutral-300/80'
              }`}
            >
              <div className="flex items-center gap-4">
                {/* Technical Drawing Inset */}
                <div className="flex h-20 w-28 shrink-0 items-center justify-center bg-white p-2">
                  <svg className="h-12 w-16 stroke-neutral-900 stroke-[1.8] fill-none" viewBox="0 0 64 48">
                    <polygon points="6,10 20,10 20,24 58,24 58,38 6,38" />
                  </svg>
                </div>
                {/* Label */}
                <span className="text-sm sm:text-base font-bold text-neutral-900">Vinkel</span>
              </div>

              {/* Checkbox / Toggle Marker */}
              <div className="mr-2">
                <div
                  className={`size-5 rounded-none border transition-colors flex items-center justify-center ${
                    angle === 'vinkel'
                      ? 'border-[#42b732] bg-[#42b732] text-white shadow-xs'
                      : 'border-neutral-400 bg-white group-hover:border-neutral-600'
                  }`}
                >
                  {angle === 'vinkel' && (
                    <i className="fa-solid fa-check text-[11px]"></i>
                  )}
                </div>
              </div>
            </div>

            {/* Option 2: Rektangulær */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => setAngle('rektangulaer')}
              onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') setAngle('rektangulaer'); }}
              className={`group relative flex cursor-pointer items-center justify-between p-3 transition-all duration-200 ${
                angle === 'rektangulaer'
                  ? 'bg-[#ECECEC] ring-2 ring-[#42b732] shadow-sm'
                  : 'bg-[#ECECEC] hover:bg-neutral-300/80'
              }`}
            >
              <div className="flex items-center gap-4">
                {/* Technical Drawing Inset */}
                <div className="flex h-20 w-28 shrink-0 items-center justify-center bg-white p-2">
                  <svg className="h-12 w-20 stroke-neutral-900 stroke-[1.8] fill-none" viewBox="0 0 80 48">
                    <rect x="8" y="16" width="64" height="16" />
                  </svg>
                </div>
                {/* Label */}
                <span className="text-sm sm:text-base font-bold text-neutral-900">Rektangulær</span>
              </div>

              {/* Checkbox / Toggle Marker */}
              <div className="mr-2">
                <div
                  className={`size-5 rounded-none border transition-colors flex items-center justify-center ${
                    angle === 'rektangulaer'
                      ? 'border-[#42b732] bg-[#42b732] text-white shadow-xs'
                      : 'border-neutral-400 bg-white group-hover:border-neutral-600'
                  }`}
                >
                  {angle === 'rektangulaer' && (
                    <i className="fa-solid fa-check text-[11px]"></i>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between pt-6">
            <button
              type="button"
              onClick={() => setStep(3)}
              className="px-5 py-2.5 rounded-none bg-stone-200 hover:bg-stone-300 text-stone-800 font-bold transition-all cursor-pointer"
            >
              Tilbage
            </button>
            <button
              type="button"
              onClick={() => setStep(5)}
              className="px-8 py-3.5 rounded-none bg-[#42b732] hover:bg-[#389e2b] text-white text-base font-extrabold transition-all flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 cursor-pointer"
            >
              Få din uforpligtende pris <i className="fa-solid fa-calculator"></i>
            </button>
          </div>
        </div>
      )}

      {/* STEP 5: Resultat & Uforpligtende Tilbud */}
      {step === 5 && (
        <div className="max-w-4xl mx-auto space-y-8 animate-fadeIn">
          
          {/* Estimate Display Box */}
          <div className="bg-stone-900 p-6 sm:p-8 rounded-none text-center shadow-xl text-white relative overflow-hidden border-t-4 border-[#42b732]">
            <div className="text-xs uppercase tracking-widest font-bold text-[#42b732] mb-1">
              Vejledende Prisestimat
            </div>
            <div className="text-3xl sm:text-5xl font-extrabold tracking-tight my-2 text-white">
              {estimate.total.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-lg sm:text-2xl text-[#42b732]">kr.</span>
            </div>
            <div className="text-xs text-stone-300">
              Inkl. moms, materialer og montering ({estimate.unitPriceM2.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr./m² grundplan)
            </div>

            {/* Summary of the 4 choices */}
            <div className="mt-6 pt-4 border-t border-stone-800 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-white text-left sm:text-center">
              <div className="bg-stone-800/60 p-2.5 rounded">
                <span className="text-stone-400 block text-[11px] uppercase tracking-wider font-semibold">1. Tagtype</span>
                <span className="font-bold text-white text-sm">{activeRoof.name}</span>
              </div>
              <div className="bg-stone-800/60 p-2.5 rounded">
                <span className="text-stone-400 block text-[11px] uppercase tracking-wider font-semibold">2. Hældning</span>
                <span className="font-bold text-white text-sm">{activeSlope.name}</span>
              </div>
              <div className="bg-stone-800/60 p-2.5 rounded">
                <span className="text-stone-400 block text-[11px] uppercase tracking-wider font-semibold">3. Grundplan</span>
                <span className="font-bold text-white text-sm">{areaM2} m²</span>
              </div>
              <div className="bg-stone-800/60 p-2.5 rounded">
                <span className="text-stone-400 block text-[11px] uppercase tracking-wider font-semibold">4. Tagform</span>
                <span className="font-bold text-white text-sm">{activeAngle.name}</span>
              </div>
            </div>

            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-xs text-stone-400 hover:text-white underline cursor-pointer"
              >
                ← Juster dine valg
              </button>
            </div>
          </div>

          {/* Lead Capture Form */}
          <div className="bg-white p-6 sm:p-8 rounded-none space-y-6 border border-stone-200">
            <div className="text-center max-w-lg mx-auto">
              <h4 className="text-xl sm:text-2xl font-bold text-stone-900">
                Få dit uforpligtende tilbud
              </h4>
              <p className="text-xs sm:text-sm text-stone-600 mt-1">
                Udfyld formularen nedenfor, så vender vi hurtigst muligt tilbage med en præcis gennemgang og et uforpligtende tilbud.
              </p>
            </div>

            {submitStatus === 'success' ? (
              <div className="p-6 rounded-none bg-emerald-50 border border-emerald-200 text-emerald-900 text-center space-y-3">
                <i className="fa-solid fa-circle-check text-4xl text-[#42b732]"></i>
                <h5 className="text-xl font-bold">Tak for din henvendelse!</h5>
                <p className="text-sm">
                  Vi har modtaget dine oplysninger og vender hurtigst muligt tilbage med et uforpligtende tilbud på dit nye tag.
                </p>
                <div className="pt-2">
                  <a
                    href={COMPANY_INFO.phoneTel}
                    className="inline-flex items-center gap-2 text-xs font-bold text-[#42b732] hover:underline"
                  >
                    Ring gerne direkte på {COMPANY_INFO.phoneFormatted} hvis du har spørgsmål
                  </a>
                </div>
              </div>
            ) : (
              <form onSubmit={handleFormSubmit} className="space-y-4">
                
                {/* Antispam Honeypot */}
                <div className="sr-only absolute" aria-hidden="true">
                  <label htmlFor="website_url">Honeypot</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={formState.website_url}
                    onChange={handleInputChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-stone-700 mb-1">
                      Navn <span className="text-[#42b732]">*</span>
                    </label>
                    <input
                      type="text"
                      name="Navn"
                      required
                      value={formState.Navn}
                      onChange={handleInputChange}
                      placeholder="Dit fulde navn"
                      className="w-full px-3.5 py-2.5 rounded-none bg-white border border-stone-300 text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#42b732] focus:border-[#42b732]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-stone-700 mb-1">
                      E-mail <span className="text-[#42b732]">*</span>
                    </label>
                    <input
                      type="email"
                      name="Mail"
                      required
                      value={formState.Mail}
                      onChange={handleInputChange}
                      placeholder="din@email.dk"
                      className="w-full px-3.5 py-2.5 rounded-none bg-white border border-stone-300 text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#42b732] focus:border-[#42b732]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-stone-700 mb-1">
                      Telefon / Mobil <span className="text-[#42b732]">*</span>
                    </label>
                    <input
                      type="tel"
                      name="Mobil"
                      required
                      value={formState.Mobil}
                      onChange={handleInputChange}
                      placeholder="F.eks. 28 68 74 50"
                      className="w-full px-3.5 py-2.5 rounded-none bg-white border border-stone-300 text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#42b732] focus:border-[#42b732]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-stone-700 mb-1">
                      Postnummer <span className="text-[#42b732]">*</span>
                    </label>
                    <input
                      type="text"
                      name="Postnummer"
                      required
                      value={formState.Postnummer}
                      onChange={handleInputChange}
                      placeholder="F.eks. 8464"
                      className="w-full px-3.5 py-2.5 rounded-none bg-white border border-stone-300 text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#42b732] focus:border-[#42b732]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-1">
                    Adresse (valgfri)
                  </label>
                  <input
                    type="text"
                    name="Adresse"
                    value={formState.Adresse}
                    onChange={handleInputChange}
                    placeholder="Gadenavn og husnummer"
                    className="w-full px-3.5 py-2.5 rounded-none bg-white border border-stone-300 text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#42b732] focus:border-[#42b732]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-1">
                    Evt. bemærkninger / spørgsmål
                  </label>
                  <textarea
                    name="Besked"
                    rows="3"
                    value={formState.Besked}
                    onChange={handleInputChange}
                    placeholder="Skriv evt. hvis du har specielle ønsker, eksisterende tagforhold eller tidsplan..."
                    className="w-full px-3.5 py-2 rounded-none bg-white border border-stone-300 text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#42b732] focus:border-[#42b732]"
                  ></textarea>
                </div>

                {/* Vedhæft billeder af hus og tag */}
                <div className="pt-1">
                  <label className="block text-xs font-semibold text-stone-700 mb-1">
                    Vedhæft evt. 1-5 billeder af dit hus på afstand, hvor man kan se taget
                  </label>
                  <div className="border-2 border-dashed border-stone-300 hover:border-[#42b732] transition-colors p-4 text-center bg-stone-50/50">
                    <input
                      type="file"
                      id="prisberegner-billeder"
                      name="billeder"
                      multiple
                      accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                      onChange={handleFileChange}
                      disabled={files.length >= 5}
                      className="hidden"
                    />
                    <label
                      htmlFor="prisberegner-billeder"
                      className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-none transition-colors ${
                        files.length >= 5
                          ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                          : 'bg-stone-900 hover:bg-stone-800 text-white cursor-pointer'
                      }`}
                    >
                      <i className="fa-solid fa-cloud-arrow-up"></i>
                      {files.length >= 5 ? 'Maks 5 billeder valgt' : 'Vælg billeder'}
                    </label>
                    <p className="text-[11px] text-stone-500 mt-2">
                      Maks. 5 billeder (maks. 10 MB pr. fil, JPG, PNG eller WEBP)
                    </p>
                  </div>

                  {fileError && (
                    <p className="text-xs text-red-600 mt-1.5 font-medium flex items-center gap-1">
                      <i className="fa-solid fa-circle-exclamation"></i>
                      {fileError}
                    </p>
                  )}

                  {files.length > 0 && (
                    <div className="mt-2.5 space-y-1.5">
                      <p className="text-[11px] font-bold text-stone-700 uppercase tracking-wide">
                        Valgte filer ({files.length}/5):
                      </p>
                      <ul className="space-y-1">
                        {files.map((file, idx) => (
                          <li
                            key={`${file.name}-${idx}`}
                            className="flex items-center justify-between bg-white border border-stone-200 px-3 py-1.5 text-xs text-stone-700"
                          >
                            <span className="truncate max-w-[260px] sm:max-w-md font-medium">
                              <i className="fa-solid fa-image text-[#42b732] mr-2"></i>
                              {file.name} <span className="text-stone-400 text-[10px]">({(file.size / (1024 * 1024)).toFixed(1)} MB)</span>
                            </span>
                            <button
                              type="button"
                              onClick={() => removeFile(idx)}
                              className="text-stone-400 hover:text-red-600 text-sm p-1 ml-2 transition-colors cursor-pointer"
                              title="Fjern billede"
                            >
                              <i className="fa-solid fa-xmark"></i>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Checkboxes matching dansktagbyg */}
                <div className="space-y-3 pt-2">
                  <label className="flex items-start gap-3 cursor-pointer text-xs sm:text-sm text-stone-700">
                    <input
                      type="checkbox"
                      name="Finansiering"
                      checked={formState.Finansiering}
                      onChange={handleInputChange}
                      className="w-4 h-4 accent-[#42b732] rounded mt-0.5"
                    />
                    <span>Har du finansieringen på plads og ønsker et tilbud?</span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer text-xs sm:text-sm text-stone-700">
                    <input
                      type="checkbox"
                      name="AccepterBetingelser"
                      required
                      checked={formState.AccepterBetingelser}
                      onChange={handleInputChange}
                      className="w-4 h-4 accent-[#42b732] rounded mt-0.5"
                    />
                    <span>
                      Jeg har læst og accepteret vilkårene for brug af oplysninger og privatlivspolitik. <span className="text-[#42b732]">*</span>
                    </span>
                  </label>
                </div>

                {submitStatus === 'error' && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded">
                    Der opstod en fejl ved afsendelsen. Prøv venligst igen eller ring direkte på {COMPANY_INFO.phoneFormatted}.
                  </div>
                )}

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 rounded-none bg-[#42b732] hover:bg-[#389e2b] text-white font-extrabold text-base tracking-wide transition-all shadow-lg hover:shadow-xl disabled:opacity-50 cursor-pointer flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <i className="fa-solid fa-circle-notch fa-spin"></i> Sender tilbudsforespørgsel...
                      </>
                    ) : (
                      <>
                        Send <i className="fa-solid fa-paper-plane"></i>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Footer Info Box */}
      <div className="mt-12 pt-8 border-t border-stone-200 max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 text-center text-stone-600 text-xs">
        <div className="flex items-center justify-center gap-3">
          <i className="fa-solid fa-shield-halved text-[#42b732] text-xl"></i>
          <span className="text-left">Dækket af Byg Garanti og op til 30 års produktgaranti</span>
        </div>
        <div className="flex items-center justify-center gap-3">
          <i className="fa-solid fa-star text-amber-500 text-xl"></i>
          <span className="text-left">4,8/5 stjerner baseret på +150 tilfredse kunder</span>
        </div>
        <div className="flex items-center justify-center gap-3">
          <i className="fa-solid fa-file-invoice text-[#42b732] text-xl"></i>
          <span className="text-left">100% uforpligtende prisoverslag og gratis tagtjek</span>
        </div>
      </div>

    </div>
  );
}
